import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { 
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Rating,
  Chip,
  Avatar,
  IconButton,
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Divider,
  Paper
} from '@mui/material';
import SeoHead from '../components/common/SeoHead';
import { styled } from '@mui/material/styles';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { db } from '../firebase/firebase';
import { collection, getDocs, query, limit, orderBy, where, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

// 导入罗盘图片
import luopanImage from '../assets/images/collections/luopan.png';
import bgImage from '../assets/images/collections/Bg.png';
import textImg from '../assets/images/collections/text01.png';
import L1Image from '../assets/images/Luopan/L1.png';
import L2Image from '../assets/images/Luopan/L2.png';
import L3Image from '../assets/images/Luopan/L3.png';
import L4Image from '../assets/images/Luopan/L4.png';
import L5Image from '../assets/images/Luopan/L5.png';
// 导入命理师背景图片
import for1Image from '../assets/images/fortuneteller/for1.jpg';
import for2Image from '../assets/images/fortuneteller/for2.jpg';

// 页面样式组件
const PageContainer = styled(Box)(({ theme }) => ({
  padding: 0,
  position: 'relative',
  overflowX: 'hidden',
}));

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '50vh',
  minHeight: '400px',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  textAlign: 'center',
  marginBottom: '3rem',
  overflow: 'hidden',
}));

const HeroImage = styled('img')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  filter: 'brightness(0.7)',
  zIndex: 0,
}));

const HeroContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  padding: '0 1rem',
  maxWidth: '800px',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 'bold',
  textAlign: 'center',
  marginBottom: '2.5rem',
  position: 'relative',
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '3px',
    background: theme.palette.primary.main,
    borderRadius: '3px',
  }
}));

const MasterSection = styled(Box)(({ theme }) => ({
  padding: '3rem 0 5rem',
  position: 'relative',
  background: '#fcfcfc',
  boxShadow: '0 10px 30px rgba(0,0,0,0.03), 0 1px 8px rgba(0,0,0,0.06)',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'radial-gradient(circle at top right, rgba(150, 120, 182, 0.08), transparent 70%)',
    zIndex: 0,
  },
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '30%',
    background: 'linear-gradient(to top, rgba(150, 120, 182, 0.05), transparent)',
    zIndex: 0,
  }
}));

const LuopanContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '500px', // 固定宽度容器
  height: '500px', // 增加高度以容纳更多图标
  margin: '0 auto', // 居中对齐
  overflow: 'visible',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(150,120,182,0.1) 0%, rgba(150,120,182,0) 70%)',
    zIndex: 0
  },
  // 保留一些基本的响应式调整，但使用固定单位
  [theme.breakpoints.down('lg')]: {
    width: '460px',
    height: '460px',
  },
  [theme.breakpoints.down('md')]: {
    width: '400px',
    height: '400px',
    margin: '0 auto', // 中心对齐
  },
  [theme.breakpoints.down('sm')]: {
    width: '320px',
    height: '320px',
  }
}));

const RotatingImage = styled('img')(({ theme }) => ({
  width: '380px', // 固定宽度
  height: 'auto',
  display: 'block',
  position: 'absolute',
  left: '50%', // 中心对齐
  top: '50%',
  transform: 'translate(-50%, -50%)',
  transformOrigin: 'center',
  animation: 'rotate 20s linear infinite',
  filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.2))',
  zIndex: 1,
  WebkitTransformStyle: 'preserve-3d', // 优化在Safari的旋转性能
  '@keyframes rotate': {
    '0%': {
      transform: 'translate(-50%, -50%) rotate(0deg)',
    },
    '100%': {
      transform: 'translate(-50%, -50%) rotate(360deg)',
    },
  },
  '@-webkit-keyframes rotate': { // Webkit浏览器的兼容性
    '0%': {
      transform: 'translate(-50%, -50%) rotate(0deg)',
    },
    '100%': {
      transform: 'translate(-50%, -50%) rotate(360deg)',
    },
  },
  // 严格固定的响应式设计
  [theme.breakpoints.down('lg')]: {
    width: '360px',
  },
  [theme.breakpoints.down('md')]: {
    width: '320px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '280px',
  }
}));

// 移动中的装饰图标
const MovingLuopanIcon = styled('img')(({ theme, index }) => {
  // 为每个图标定义不同的动画配置 - 增加布朗运动效果
  const animationConfigs = [
    {
      name: 'moveIcon1',
      keyframes: {
        '0%': { transform: 'translate(0, 0) rotate(0deg)' },
        '10%': { transform: 'translate(30px, -25px) rotate(15deg)' },
        '20%': { transform: 'translate(45px, 10px) rotate(8deg)' },
        '30%': { transform: 'translate(20px, 35px) rotate(-5deg)' },
        '40%': { transform: 'translate(-15px, 25px) rotate(-12deg)' },
        '50%': { transform: 'translate(-40px, -5px) rotate(-20deg)' },
        '60%': { transform: 'translate(-25px, -40px) rotate(-10deg)' },
        '70%': { transform: 'translate(5px, -35px) rotate(5deg)' },
        '80%': { transform: 'translate(35px, -15px) rotate(15deg)' },
        '90%': { transform: 'translate(25px, 20px) rotate(8deg)' },
        '100%': { transform: 'translate(0, 0) rotate(0deg)' }
      },
      duration: '28s',
      delay: '0s'
    },
    {
      name: 'moveIcon2',
      keyframes: {
        '0%': { transform: 'translate(0, 0) rotate(0deg)' },
        '10%': { transform: 'translate(-35px, -15px) rotate(-12deg)' },
        '20%': { transform: 'translate(-20px, -45px) rotate(-18deg)' },
        '30%': { transform: 'translate(15px, -40px) rotate(-5deg)' },
        '40%': { transform: 'translate(40px, -10px) rotate(8deg)' },
        '50%': { transform: 'translate(30px, 30px) rotate(20deg)' },
        '60%': { transform: 'translate(-5px, 45px) rotate(12deg)' },
        '70%': { transform: 'translate(-45px, 15px) rotate(0deg)' },
        '80%': { transform: 'translate(-35px, -25px) rotate(-10deg)' },
        '90%': { transform: 'translate(-10px, -40px) rotate(-15deg)' },
        '100%': { transform: 'translate(0, 0) rotate(0deg)' }
      },
      duration: '32s',
      delay: '-5s'
    },
    {
      name: 'moveIcon3',
      keyframes: {
        '0%': { transform: 'translate(0, 0) rotate(0deg)' },
        '10%': { transform: 'translate(40px, 10px) rotate(10deg)' },
        '20%': { transform: 'translate(25px, 45px) rotate(18deg)' },
        '30%': { transform: 'translate(-15px, 35px) rotate(8deg)' },
        '40%': { transform: 'translate(-45px, -5px) rotate(-5deg)' },
        '50%': { transform: 'translate(-30px, -40px) rotate(-15deg)' },
        '60%': { transform: 'translate(10px, -45px) rotate(-8deg)' },
        '70%': { transform: 'translate(45px, -15px) rotate(5deg)' },
        '80%': { transform: 'translate(30px, 25px) rotate(12deg)' },
        '90%': { transform: 'translate(-20px, 35px) rotate(5deg)' },
        '100%': { transform: 'translate(0, 0) rotate(0deg)' }
      },
      duration: '26s',
      delay: '-8s'
    },
    {
      name: 'moveIcon4',
      keyframes: {
        '0%': { transform: 'translate(0, 0) rotate(0deg)' },
        '10%': { transform: 'translate(-25px, -35px) rotate(-15deg)' },
        '20%': { transform: 'translate(20px, -45px) rotate(-5deg)' },
        '30%': { transform: 'translate(45px, -15px) rotate(10deg)' },
        '40%': { transform: 'translate(35px, 30px) rotate(20deg)' },
        '50%': { transform: 'translate(-10px, 40px) rotate(10deg)' },
        '60%': { transform: 'translate(-45px, 5px) rotate(-5deg)' },
        '70%': { transform: 'translate(-35px, -30px) rotate(-15deg)' },
        '80%': { transform: 'translate(5px, -40px) rotate(-8deg)' },
        '90%': { transform: 'translate(40px, -10px) rotate(5deg)' },
        '100%': { transform: 'translate(0, 0) rotate(0deg)' }
      },
      duration: '30s',
      delay: '-12s'
    },
    {
      name: 'moveIcon5',
      keyframes: {
        '0%': { transform: 'translate(0, 0) rotate(0deg)' },
        '10%': { transform: 'translate(40px, 25px) rotate(15deg)' },
        '20%': { transform: 'translate(10px, 45px) rotate(20deg)' },
        '30%': { transform: 'translate(-35px, 30px) rotate(10deg)' },
        '40%': { transform: 'translate(-45px, -15px) rotate(-8deg)' },
        '50%': { transform: 'translate(-15px, -45px) rotate(-20deg)' },
        '60%': { transform: 'translate(25px, -40px) rotate(-15deg)' },
        '70%': { transform: 'translate(45px, -5px) rotate(-5deg)' },
        '80%': { transform: 'translate(35px, 35px) rotate(5deg)' },
        '90%': { transform: 'translate(-5px, 45px) rotate(12deg)' },
        '100%': { transform: 'translate(0, 0) rotate(0deg)' }
      },
      duration: '34s',
      delay: '-10s'
    }
  ];

  // 获取当前图标的配置
  const config = animationConfigs[index % animationConfigs.length];

  // 定义动态关键帧动画
  const keyframesStr = {};
  for (const [key, value] of Object.entries(config.keyframes)) {
    keyframesStr[key] = value;
  }

  return {
    position: 'absolute',
    width: '80px',
    height: 'auto',
    zIndex: 2,
    filter: 'drop-shadow(0 5px 10px rgba(0,0,0,0.15))',
    transition: 'transform 0.3s ease',
    animation: `${config.name} ${config.duration} ease-in-out infinite ${config.delay}`,
    '@keyframes moveIcon1': keyframesStr,
    '@keyframes moveIcon2': keyframesStr,
    '@keyframes moveIcon3': keyframesStr,
    '@keyframes moveIcon4': keyframesStr,
    '@keyframes moveIcon5': keyframesStr,
    '&:hover': {
      transform: 'scale(1.1)'
    },
    [theme.breakpoints.down('md')]: {
      width: '70px',
    },
    [theme.breakpoints.down('sm')]: {
      width: '60px',
    }
  };
});

// 命理师样式已移除 - 使用一般组件实现

// 已移除命理师类别数据

// 命理师列表页面组件
const FortuneTeller = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  
  // 状态管理
  const [tellers, setTellers] = useState([]);
  const [featuredTellers, setFeaturedTellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [heroBgImage, setHeroBgImage] = useState(bgImage);
  
  // 添加SEO结构化数据
  const fortuneTellerSchemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "命理咨询",
    "provider": {
      "@type": "Organization",
      "name": "DestinyCrystal命理水晶",
      "url": "https://destiny-crystal.com"
    },
    "description": "专业命理师提供八字命盘、紫微斗数、事业财运等精准分析，帮助您获得命理指引，做出更明智的人生决策。",
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "SGD",
      "lowPrice": "98",
      "highPrice": "599",
      "offerCount": "5"
    }
  };

  // 直接从Firestore获取所有命理师数据
  useEffect(() => {
    const fetchFortuneTellers = async () => {
      // 设置加载状态
      setLoading(true);
      
      try {
        console.log("直接从Firestore获取命理师数据");
        console.log("Firestore实例:", db);
        
        // 尝试更简单的查询，不使用任何过滤条件
        try {
          console.log("尝试获取所有命理师数据，不使用过滤条件");
          const fortuneTellersRef = collection(db, "fortuneTellers");
          
          // 首先尝试最简单的查询，不使用任何排序或限制
          const simpleQuery = query(fortuneTellersRef);
          console.log("执行简单查询...");
          
          try {
            console.log("尝试获取数据...");
            const querySnapshot = await getDocs(simpleQuery);
            console.log("查询成功执行，结果:", querySnapshot);
            
            if (!querySnapshot.empty) {
              console.log("查询结果不为空，文档数量:", querySnapshot.size);
              const realTellers = [];
              querySnapshot.forEach((doc) => {
                console.log("处理文档:", doc.id);
                const data = doc.data();
                console.log("文档数据:", data);
                realTellers.push({
                  id: doc.id,
                  name: data.name || "未命名命理师",
                  title: data.title || "命理师",
                  photoURL: data.photoURL || 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg',
                  rating: data.rating || 4.5,
                  price: data.price || 299,
                  specialties: data.specialties || [],
                  featured: data.featured || false,
                  ...data
                });
              });
              
              // 如果能够获取到真实数据，则使用真实数据
              if (realTellers.length > 0) {
                const realFeaturedTellers = realTellers.filter(t => t.featured);
                setTellers(realTellers);
                setFeaturedTellers(realFeaturedTellers.length > 0 ? realFeaturedTellers : []);
                console.log("成功从Firestore加载命理师数据", realTellers.length);
                return;
              } else {
                console.log("解析后的命理师数组为空");
              }
            } else {
              console.log("查询结果为空");
            }
          } catch (getDocsError) {
            console.error("执行getDocs查询失败:", getDocsError);
            console.error("完整错误:", JSON.stringify(getDocsError));
          }
        } catch (queryError) {
          console.error("简单查询失败:", queryError);
        }
        
        // 如果简单查询失败，尝试查询具体的文档ID
        try {
          console.log("尝试获取特定文档ID");
          const docRef = doc(db, "fortuneTellers", "sample-teller-1");
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            console.log("文档存在:", docSnap.data());
          } else {
            console.log("文档不存在");
          }
        } catch (docError) {
          console.error("获取特定文档失败:", docError);
        }
        
        // 如果数据获取失败，显示空列表
        console.log("未找到命理师数据，显示空列表");
        setTellers([]);
        setFeaturedTellers([]);
      } catch (err) {
        console.error("获取命理师数据失败: ", err);
        setError("获取命理师信息时出错，请稍后再试。");
        setTellers([]);
        setFeaturedTellers([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFortuneTellers();
  }, []);

  // 选择命理师处理
  const handleTellerSelect = (teller) => {
    // 导航到详情页
    navigate(`/fortune-teller/${teller.id}`);
  };

  return (
    <PageContainer>
      {/* SEO优化 */}
      <SeoHead
        title="专业命理师预约 | 八字分析、紫微斗数、塔罗牌 | DestinyCrystal"
        description="预约DestinyCrystal专业命理师，提供八字命盘、紫微斗数、事业财运等专业分析，获得精准命理指引，帮助您做出更明智的人生决策。"
        canonicalUrl="https://destiny-crystal.com/fortune-teller"
        imageUrl="https://destiny-crystal.com/assets/images/collections/Bg.png"
        keywords={["命理师", "八字命盘", "紫微斗数", "塔罗牌", "命理咨询", "事业运势", "爱情运势"]}
        schemaData={fortuneTellerSchemaData}
      />
      {/* Hero Section */}
      <HeroSection>
        <HeroImage src={heroBgImage} alt="Fortune Background" />
        <HeroContent>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            命理解析，知己知彼
          </Typography>
          <Typography variant="h5" gutterBottom>
            Fortune Reading & Destiny Analysis
          </Typography>
          <Box display="flex" justifyContent="center" mt={3}>
            <Button 
              variant="outlined" 
              color="inherit"
              sx={{ 
                borderColor: 'white',
                '&:hover': { borderColor: 'white', background: 'rgba(255,255,255,0.1)' },
                px: 3,
                py: 1
              }}
            >
              探索更多
            </Button>
          </Box>
        </HeroContent>
      </HeroSection>
      
      {/* 命理师展示与罗盘视觉部分 */}
      <Box sx={{ position: 'relative', py: 6, backgroundColor: '#f9f6fc' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" align="center" gutterBottom>
            我们的命理师
          </Typography>
          <Typography variant="h5" align="center" gutterBottom>
            Our Fortune Tellers
          </Typography>
          <Divider sx={{ width: '60px', height: '4px', backgroundColor: '#9c27b0', mb: 5, mx: 'auto', borderRadius: '2px' }} />
          
          <Grid container spacing={4}>
            {/* 左侧命理师列表 */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                backgroundImage: `url(${for1Image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
                p: 4, 
                borderRadius: 3,
                boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                height: '100%',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(255, 255, 255, 0.85)',
                  borderRadius: 3,
                  zIndex: 0
                }
              }}>
                <Typography 
                  variant="h5" 
                  gutterBottom 
                  sx={{ 
                    mb: 4, 
                    position: 'relative', 
                    zIndex: 1, 
                    fontWeight: 'bold',
                    textAlign: 'center',
                    '&:after': {
                      content: '""',
                      display: 'block',
                      width: '60px',
                      height: '3px',
                      backgroundColor: 'primary.main',
                      margin: '10px auto 0'
                    }
                  }}
                >
                  我们的命理师
                </Typography>
                <Grid container spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
                  {tellers.length > 0 ? (
                    tellers.map(teller => (
                      <Grid item xs={12} sm={6} md={4} key={teller.id}>
                        <Box 
                          onClick={() => handleTellerSelect(teller)}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            cursor: 'pointer',
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.5)',
                            transition: 'all 0.3s ease',
                            height: '100%',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.8)',
                              transform: 'translateY(-5px)',
                              boxShadow: '0 8px 20px rgba(156, 39, 176, 0.15)'
                            }
                          }}
                        >
                          <Avatar
                            src={teller.photoURL}
                            alt={teller.name}
                            sx={{ 
                              width: 100, 
                              height: 100, 
                              mb: 2, 
                              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                              border: '3px solid white' 
                            }}
                          />
                          <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                            {teller.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {teller.title || "命理师"}
                          </Typography>
                          <Rating
                            value={teller.rating || 0}
                            precision={0.5}
                            readOnly
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                          {/* 专长标签 */}
                          {teller.specialties && teller.specialties.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.5, mt: 1, mb: 1 }}>
                              {teller.specialties.slice(0, 2).map((specialty, index) => (
                                <Chip 
                                  key={index} 
                                  label={specialty} 
                                  size="small" 
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              ))}
                            </Box>
                          )}
                          
                          {/* 价格显示 */}
                          {teller.price && (
                            <Typography 
                              variant="body2" 
                              color="primary" 
                              fontWeight="bold"
                              sx={{ mt: 1 }}
                            >
                              S${teller.price}
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    ))
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 4 }}>
                      {loading ? (
                        <CircularProgress size={40} />
                      ) : (
                        <Typography>暂无命理师信息</Typography>
                      )}
                    </Box>
                  )}
                </Grid>
                
                {/* 按要求移除"查看全部命理师"按钮 */}
              </Box>
            </Grid>
            
            {/* 右侧罗盘展示 */}
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                position: 'relative', 
                height: '500px', 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                backgroundImage: `url(${for2Image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: 3,
                boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                overflow: 'hidden'
              }}>
                {/* 半透明遮罩层 */}
                <Box 
                  sx={{
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    zIndex: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    borderRadius: 3
                  }}
                />
                
                {/* 旋转的罗盘 */}
                <RotatingImage
                  src={luopanImage}
                  alt="Luopan Compass"
                />
                
                {/* 周围的五个图标 - 使用动态位置和动画 */}
                <MovingLuopanIcon
                  src={L1Image}
                  alt="L1"
                  index={0}
                  sx={{
                    top: '15%',
                    left: '25%'
                  }}
                />
                <MovingLuopanIcon
                  src={L2Image}
                  alt="L2"
                  index={1}
                  sx={{
                    top: '20%',
                    right: '20%'
                  }}
                />
                <MovingLuopanIcon
                  src={L3Image}
                  alt="L3"
                  index={2}
                  sx={{
                    bottom: '25%',
                    left: '20%'
                  }}
                />
                <MovingLuopanIcon
                  src={L4Image}
                  alt="L4"
                  index={3}
                  sx={{
                    bottom: '15%',
                    right: '25%'
                  }}
                />
                <MovingLuopanIcon
                  src={L5Image}
                  alt="L5"
                  index={4}
                  sx={{
                    top: '50%',
                    right: '15%'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      
      {/* Call to Action */}
      <Box sx={{ 
        position: 'relative',
        py: 10, 
        textAlign: 'center',
        color: 'white',
        overflow: 'hidden'
      }}>
        {/* 背景图 */}
        <Box 
          component="img"
          src={require('../assets/images/Luopan/banner1.png')}
          alt="Banner" 
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
            filter: 'brightness(0.7)'
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h4" gutterBottom>
            预约您的专属命理咨询
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 4, fontSize: '1.1rem' }}>
            无论您面临何种人生困惑，我们的专业命理师都能为您提供清晰的指引和建议。
          </Typography>
        </Container>
      </Box>
    </PageContainer>
  );
};

export default FortuneTeller;